import { useAuthStore } from "~/stores/auth";
import type { UserToken } from "~/models/user";
import type { ApiError } from "~/models/error";

export default defineNuxtPlugin(() => {
  const authStore = useAuthStore();
  const config = useRuntimeConfig();
  const baseURL = import.meta.server
    ? (config.apiUrlInternal || config.public.apiUrl || "http://localhost:4200/api")
    : (config.public.apiUrl || "http://localhost:4200/api");

  const apiRequest = async <T>(
    method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE",
    url: string,
    body?: Record<string, unknown>,
    credentials?: "same-origin" | "include" | "omit",
  ): Promise<T> => {
    try {
      const urlPath = `${baseURL}${url}`;
      const headers: Record<string, string> = {};
      if (authStore.token) {
        headers.Authorization = `Bearer ${authStore.token}`;
      }
      const response = await $fetch<T>(urlPath, {
        method,
        body,
        headers,
        credentials,
      });
      return response;
    } catch (error: unknown) {
      if (isApiError(error) && error.status === 401) {
        try {
          await refreshToken();
          const retryHeaders: Record<string, string> = {};
          if (authStore.token) {
            retryHeaders.Authorization = `Bearer ${authStore.token}`;
          }
          const retryResponse = await $fetch<T>(`${baseURL}${url}`, {
            method,
            body,
            headers: retryHeaders,
            credentials,
          });
          return retryResponse;
        } catch {
          authStore.logout();
          throw new Error("Session expired. Please log in again.");
        }
      } else {
        throw error;
      }
    }
  };

  const refreshToken = async (): Promise<void> => {
    const response: UserToken = await $fetch(`${baseURL}/user/refresh`, {
      method: "POST",
      credentials: "include",
    });
    authStore.refreshUserToken(response.token);
  };

  return {
    provide: {
      api: {
        get: <T>(
          url: string,
          credentials?: "same-origin" | "include" | "omit",
        ) => apiRequest<T>("GET", url, undefined, credentials),
        post: <T>(
          url: string,
          body: Record<string, unknown>,
          credentials?: "same-origin" | "include" | "omit",
        ) => apiRequest<T>("POST", url, body, credentials),
        put: <T>(
          url: string,
          body: Record<string, unknown>,
          credentials?: "same-origin" | "include" | "omit",
        ) => apiRequest<T>("PUT", url, body, credentials),
        patch: <T>(
          url: string,
          body: Record<string, unknown>,
          credentials?: "same-origin" | "include" | "omit",
        ) => apiRequest<T>("PATCH", url, body, credentials),
        del: <T>(
          url: string,
          credentials?: "same-origin" | "include" | "omit",
        ) => apiRequest<T>("DELETE", url, undefined, credentials),
      },
    },
  };
});

function isApiError(error: unknown): error is ApiError {
  return typeof error === "object" && error !== null && "status" in error;
}
